import type { FC, PropsWithChildren } from 'react';
import { createContext, useState, useReducer, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRequest } from 'loti-request';

import { LOGIN_PATH } from 'app/constants/routes';
import { fetch } from 'app/utils/fetch';
import { getApiUrl, getHeaders, getTangerineHeaders } from 'app/utils/api';
import { setUserScope } from 'app/utils/sentry';
import { initJiraLiveChat } from 'app/utils/jiraLiveChat';
import * as realTimeLogs from 'app/utils/realTime/logs';
import HappyBirthday from 'app/components/HappyBirthday';
import { hasAccessToFeature } from 'app/utils/permissions';
import createServerErrorNotification from 'app/components/ServerErrorNotification';
import { createSuccessNotification } from 'app/components/Notification';
import { initHotjar } from 'app/utils/hotjar';

import {
  LOCALSTORAGE_TOKEN_KEY,
  getBirthdayName,
  getdisplayedOverroundFromPreferences
} from './utils';
import { initialPreferences, initialState, reducer } from './reducer';
import type { LoginValues, UserContextValue } from './types';

const defaultValue: UserContextValue = {
  ...initialState,
  preferences: initialPreferences,
  updatePreferences: () => {
    console.log('update preference');
  },
  login: () => {
    console.log('login');
  },
  logout: () => {
    console.log('logout');
  },
  reset: () => {
    console.log('reset');
  },
  setLoginTheme: () => {
    console.log('set login theme');
  }
};

const UserContext = createContext(defaultValue);

// Mutable value to give access userState in portal (which hasn't access to context)
// It was added for notification component
// eslint-disable-next-line import/no-mutable-exports
let userState = initialState;

const UserProvider: FC<PropsWithChildren> = ({ children }) => {
  const [birthdayName, setBirthdayName] = useState<string>('');
  const [user, dispatch] = useReducer(reducer, initialState);
  const [loginTheme, setLoginTheme] = useState(initialPreferences.theme);
  const navigate = useNavigate();

  const { fetch: updatePreferencesFetch } = useRequest<UserPreferences>({
    url: getApiUrl('/users/preferences'),
    headers: getTangerineHeaders(),
    method: 'PATCH',
    onError: (e, _, params) => createServerErrorNotification(e, params),
    onSuccess: data => {
      dispatch({
        payload: data,
        type: 'UPDATE_PREFERENCES'
      });
    }
  });

  const updatePreferences = (newValues: Partial<UserPreferences>) => {
    updatePreferencesFetch({
      body: newValues
    });
  };

  useEffect(() => {
    userState = user;

    const userBirthdayName = user.currentUser ? getBirthdayName(user.currentUser.id) : undefined;

    if (userBirthdayName) {
      setBirthdayName(userBirthdayName);
      const birthdayTimeout = setTimeout(() => {
        setBirthdayName('');
      }, 9000);

      return () => {
        clearTimeout(birthdayTimeout);
      };
    }
  }, [user]);

  const login = useCallback(
    ({ email = '', password = '', token }: LoginValues, loginAfterSignup = false) => {
      dispatch({ type: 'AUTHENTICATION_REQUEST' });

      const headers = getHeaders();
      const body = token ? { token } : { email, psswd: password };

      fetch<User>(getApiUrl('/auth'), { method: 'POST', headers, body })
        .then(user => {
          localStorage.setItem(LOCALSTORAGE_TOKEN_KEY, user.token);
          const userType = user.company.odds_subscription;
          dispatch({
            type: 'AUTHENTICATION_SUCCESS',
            payload: {
              user,
              userType
            }
          });

          // define user for sentry
          setUserScope(user);
          // init live chat only for bookmaker and trial users
          if (hasAccessToFeature('LIVE_CHAT', userType)) {
            initJiraLiveChat(user);
          }
          // enable realtime logs only for traders
          if (hasAccessToFeature('REALTIME_LOGS', userType)) {
            realTimeLogs.enable();
          }

          if (hasAccessToFeature('HOTJAR', userType)) {
            initHotjar(user);
          }

          if (loginAfterSignup) {
            createSuccessNotification('signup-success');
          }
        })
        .catch(error => {
          console.error(error);
          if (error.error === 'email_unconfirmed') {
            dispatch({
              type: 'EMAIL_NOT_CONFIRMED_ERROR'
            });
          } else {
            dispatch({
              type: 'AUTHENTICATION_FAIL'
            });
          }
        });
    },
    [dispatch]
  );

  const logout = useCallback(() => {
    localStorage.removeItem(LOCALSTORAGE_TOKEN_KEY);
    navigate(LOGIN_PATH, { state: { from: '/' } });
    dispatch({ type: 'LOGOUT' });
    realTimeLogs.disable();
  }, [dispatch]);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET_AUTHENTICATION_ERROR' });
  }, [dispatch]);

  const value: UserContextValue = useMemo(
    () => ({
      ...user,
      preferences: user.currentUser?.preferences
        ? {
            ...user.currentUser?.preferences,
            overround_displayed: getdisplayedOverroundFromPreferences(user.currentUser?.preferences)
          }
        : {
            ...defaultValue.preferences,
            theme: loginTheme
          },
      login,
      logout,
      reset,
      updatePreferences,
      setLoginTheme
    }),
    [user, loginTheme, login, logout, reset]
  );

  return (
    <UserContext.Provider value={value}>
      {birthdayName && <HappyBirthday name={birthdayName} />}
      {children}
    </UserContext.Provider>
  );
};

UserProvider.displayName = 'UserProvider';

export default UserContext;
export { UserProvider, userState, LOCALSTORAGE_TOKEN_KEY };
